// src/pages/shelbula/workspaces/HistoryActionsMenu.jsx
import React, { useState, useEffect, useRef } from 'react';
import { MoreVertical, ListRestart, Undo, History, FileClock, RotateCcw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const HistoryActionsMenu = ({ item, workspace, onInitiateUndo, onInitiateRevertToPoint, onInitiateRestart, onInitiateCancel, onInitiateRollbackToPlan, isCompact = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef(null);

    const { status, startCommitHash, planApprovalCommitHash, endOfPhaseCommitHash, originalPlan, modelContext, commitHash, type } = item;
    const isAiPhase = type === 'AI_PHASE';
    const isActive = ['PENDING_APPROVAL', 'EXECUTING', 'PLANNING', 'RE_PLANNING'].includes(status);
    
    const handleRollbackBefore = () => {
        if (isActive) {
            onInitiateCancel(workspace, item);
        } else {
            onInitiateUndo(workspace, item);
        }
    };

    const canRollbackBefore = isAiPhase && !!startCommitHash;
    const canRollbackToPlan = isAiPhase && !!planApprovalCommitHash;
    const canRollbackAfter = isAiPhase && status === 'COMPLETED' && !!endOfPhaseCommitHash;
    const canRestart = isAiPhase && ['COMPLETED', 'FAILED'].includes(status) && !!startCommitHash && !!originalPlan && !!modelContext;
    const canRevertSimpleCommit = !isAiPhase && !!commitHash;
    const hasAnyAction = canRollbackBefore || canRollbackToPlan || canRollbackAfter || canRestart || canRevertSimpleCommit;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    if (!hasAnyAction) return isCompact ? null : <div className="w-8 h-8" />;

    const createMenuItem = (onClick, text, icon, className = '') => (
        <button
            onClick={(e) => { e.stopPropagation(); onClick(); setIsOpen(false); }}
            className={`flex items-center w-full text-left gap-3 px-3 py-2 text-sm hover:bg-primary/10 transition-colors ${className}`}
        >
            {icon}
            <span>{text}</span>
        </button>
    );

    return (
        <div className="relative" ref={menuRef}>
            <button
                onClick={(e) => { e.stopPropagation(); setIsOpen(prev => !prev); }}
                className={`${isCompact ? 'p-1' : 'p-1'} rounded-full text-on-surface-dim hover:text-primary hover:bg-primary/10 transition-colors`}
                title="Actions"
            >
                {isCompact ? <RotateCcw size={14} /> : <MoreVertical size={16} />}
            </button>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -5, scale: 0.95 }}
                        animate={{ opacity: 1, y: 5, scale: 1 }}
                        exit={{ opacity: 0, y: -5, scale: 0.95 }}
                        className="absolute right-0 mt-1 w-64 bg-surface border border-border-color rounded-md shadow-lg z-20"
                    >
                        <div className="py-1">
                            {canRevertSimpleCommit && createMenuItem(() => onInitiateRevertToPoint(workspace, item, commitHash), 'Rollback to this point', <History size={14} className="text-on-surface-dim" />, 'text-on-surface hover:text-primary')}
                            {canRollbackAfter && createMenuItem(() => onInitiateRevertToPoint(workspace, item, endOfPhaseCommitHash), 'Rollback to AFTER this step', <History size={14} className="text-on-surface-dim" />, 'text-on-surface hover:text-primary')}
                            {canRollbackToPlan && createMenuItem(() => onInitiateRollbackToPlan(workspace, item), "Rollback to this step's PLAN", <FileClock size={14} className="text-on-surface-dim" />, 'text-on-surface hover:text-primary')}
                            {canRollbackBefore && createMenuItem(handleRollbackBefore, 'Rollback to BEFORE this step', <Undo size={14} className="text-red-400" />, 'text-red-400 hover:text-red-400')}
                            {canRestart && (
                                <>
                                    {(canRollbackAfter || canRollbackToPlan || canRollbackBefore || canRevertSimpleCommit) && <div className="h-px bg-border-color/50 my-1" />}
                                    {createMenuItem(() => onInitiateRestart(workspace, item), 'Restart this Phase', <ListRestart size={14} className="text-on-surface-dim" />, 'text-on-surface hover:text-primary')}
                                </>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default HistoryActionsMenu;